import { useMemo } from "react"
import { useAppSelector } from "hooks/redux"
import { isPinnedHistoricalBatch } from "gatherers/helpers"
import { computePricesAsOfDisplay, getBatchMomentOrTimeOnly, type IntradayRelativeDay } from "helpers/intraday"
import { getToday } from "helpers/types/dates"
import type { InstrumentMetadata } from "helpers/types/instruments/metadata"
import { useDashboardId } from "../context/dashboard-id"
import { DEFAULT_CORE_STATE } from "../store/core-slice"
import type { IntradayToolbarTimesProp } from "../helpers/intraday-toolbar-times"
import { getReportedPnlQualifiers } from "../helpers/reported-pnl"
import { useIntradayState } from "./use-intraday-state"
import { useIntradayToolbarTimes } from "./use-intraday-toolbar-times"
import { useMainInstrumentFxHedge } from "./use-main-instrument-fx-hedge"

interface UseIntradayToolbarParams {
  instrumentMetadata: InstrumentMetadata | null | undefined
  baseInstrumentMetadata: InstrumentMetadata | null | undefined
}

export interface PeriodSelectorIntradayTime {
  intradayAsOf: string | null
  intradayRelativeDay: IntradayRelativeDay
}

export interface IntradayToolbar {
  toolbarTimes: IntradayToolbarTimesProp | null
  periodSelectorTime: PeriodSelectorIntradayTime
}

export function useIntradayToolbar({ instrumentMetadata, baseInstrumentMetadata }: UseIntradayToolbarParams): IntradayToolbar {
  const dashboardId = useDashboardId()
  const { instrument, base, comparisonInstrument, period, isIntraday, isEndDateIntraday, intradayPricingAsOf, intradayBatchMetadata } = useAppSelector((s) => s.dashboard.core[dashboardId] ?? DEFAULT_CORE_STATE)
  const modelId = useAppSelector((s) => s.dashboard.model[dashboardId]?.modelId ?? null)
  const [{ lastIntradayBatchId }] = useIntradayState()
  const { isFxHedged } = useMainInstrumentFxHedge(instrumentMetadata)

  const isEnabled = isIntraday && isEndDateIntraday
  const latestTimes = useIntradayToolbarTimes({ modelId, instrument, on: getToday(), isEnabled, intradayPricingAsOf, lastIntradayBatchId })

  // The panels stay on a pinned batch (open trades, comparison), so the positions time follows it; prices and P&L are not batch bound.
  const pinnedPositionsAsOf = isPinnedHistoricalBatch(intradayBatchMetadata?.id, lastIntradayBatchId)
    ? getBatchMomentOrTimeOnly(intradayBatchMetadata)?.format() ?? null
    : null
  const data = useMemo(() => (
    latestTimes && pinnedPositionsAsOf ? { ...latestTimes, positionsAsOf: pinnedPositionsAsOf } : latestTimes
  ), [latestTimes, pinnedPositionsAsOf])

  const activeBaseName = base ? baseInstrumentMetadata?.name ?? null : null
  const comparisonMainInstrumentName = comparisonInstrument ? instrumentMetadata?.name ?? null : null
  const basis = data?.reportedPnl?.basis ?? null

  const toolbarTimes = useMemo(() => {
    if (!isEnabled) return null
    return {
      data,
      qualifiers: getReportedPnlQualifiers({ activeBaseName, isFxHedged, period, basis, comparisonMainInstrumentName }),
    }
  }, [isEnabled, data, activeBaseName, isFxHedged, period, basis, comparisonMainInstrumentName])

  // Falls back to the pricing time in state while the toolbar query has no data, loading or failed.
  const pricesAsOf = toolbarTimes?.data?.pricesAsOf ?? (isEnabled ? intradayPricingAsOf : null)
  const periodSelectorTime = useMemo((): PeriodSelectorIntradayTime => {
    const display = computePricesAsOfDisplay(pricesAsOf)
    return { intradayAsOf: display?.currentAsOf ?? null, intradayRelativeDay: display?.intradayRelativeDay ?? "today" }
  }, [pricesAsOf])

  return { toolbarTimes, periodSelectorTime }
}
